import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FileText, 
  Check, 
  Copy,
  Download,
  Sparkles,
  TrendingUp,
  Target,
  Loader2,
  AlertCircle,
  ArrowRight,
  Edit3
} from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * RV.4: Generic Post-Signup Value Completion Page
 * 
 * Recovers pre-auth user input/generation after signup and delivers full value.
 * Designed to work with multiple flows (review, visibility, clarity, etc.)
 * 
 * Routes:
 * - /review/complete?rv_id=xxx&hash=yyy (Performance Review)
 * - /visibility/complete?rv_id=xxx&hash=yyy (Impact Visibility - future)
 * - /clarity/complete?session_id=xxx (Situational Clarity - future)
 */

const ValueCompletionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [copied, setCopied] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Get params from URL or sessionStorage
  const reviewId = searchParams.get('rv_id');
  const inputHash = searchParams.get('hash');

  useEffect(() => {
    // Wait for auth to complete
    if (authLoading) return;

    // If not authenticated, redirect to login with return URL
    if (!user) {
      const returnUrl = window.location.pathname + window.location.search;
      navigate(`/login?return=${encodeURIComponent(returnUrl)}`);
      return;
    }

    // Complete the review
    completeReview();
  }, [user, authLoading, reviewId, inputHash]);

  const completeReview = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Try to complete with review_id from URL
      let rvId = reviewId;
      let hash = inputHash;

      // Fallback to sessionStorage
      if (!rvId || !hash) {
        const pending = sessionStorage.getItem('rv_pending_unlock');
        if (pending) {
          const data = JSON.parse(pending);
          rvId = data.review_id;
          hash = data.input_hash;
        }
      }

      if (!rvId) {
        // No review to complete - check if we have input to generate fresh
        const savedInput = sessionStorage.getItem('rv_input');
        if (savedInput) {
          // Generate fresh review with auth
          await generateFreshReview(JSON.parse(savedInput), token);
          return;
        }
        throw new Error('No review found to complete. Please start from the beginning.');
      }

      // Complete the existing guest review
      const response = await fetch(`${API_URL}/api/review/complete/${rvId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to complete review');
      }

      const data = await response.json();
      setReviewData(data);

      // Clear sessionStorage
      sessionStorage.removeItem('rv_pending_unlock');
      sessionStorage.removeItem('rv_input');

    } catch (err) {
      console.error('Complete review error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateFreshReview = async (inputData, token) => {
    // This would call a different endpoint for authenticated generation
    // For now, redirect to the dashboard review page
    navigate('/dashboard/review');
  };

  const handleCopy = async () => {
    if (!reviewData) return;

    const achievements = reviewData.achievements
      .map(a => `• ${a.title}\n  ${a.impact}\n  Skills: ${a.skills.join(', ')}`)
      .join('\n\n');

    const devAreas = reviewData.development_areas.items
      .map(d => `• ${d}`)
      .join('\n');

    const goals = reviewData.goals.items
      .map(g => `• ${g}`)
      .join('\n');

    const text = `
PERFORMANCE REVIEW SELF-ASSESSMENT

SUMMARY
${reviewData.summary}

KEY ACHIEVEMENTS
${achievements}

DEVELOPMENT AREAS
${devAreas}

GOALS FOR NEXT PERIOD
${goals}
    `.trim();

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard/review');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-red-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">
            {authLoading ? 'Checking authentication...' : 'Unlocking your full review...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Something went wrong</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              onClick={() => navigate('/review')}
            >
              Start New Review
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!reviewData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-6" data-testid="rv-completion-page">
      <div className="max-w-3xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Your Full Review is Ready!
          </h1>
          <p className="text-slate-600">
            All sections unlocked. Edit, refine, and save to your Living CV.
          </p>
        </div>

        {/* Unlocked Badge */}
        <div className="flex justify-center gap-2 mb-8">
          {reviewData.unlocked_features?.map((feature, i) => (
            <span 
              key={i}
              className="px-3 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full flex items-center gap-1"
            >
              <Check className="w-3 h-3" />
              {feature.replace(/_/g, ' ')}
            </span>
          ))}
        </div>

        {/* Full Review Card */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-lg overflow-hidden mb-6">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                <FileText className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Performance Review</p>
                <p className="text-sm text-slate-500">Saved to your account</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy All'}
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Summary */}
            <div className="mb-6">
              <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium">Summary</h4>
              <p className="text-slate-800 leading-relaxed">{reviewData.summary}</p>
            </div>

            {/* All Achievements */}
            <div className="mb-6">
              <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-medium flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Key Achievements ({reviewData.achievements.length})
              </h4>
              <div className="space-y-3">
                {reviewData.achievements.map((achievement, index) => (
                  <div 
                    key={index}
                    className="bg-slate-50 rounded-xl p-4 border border-slate-100"
                  >
                    <p className="font-medium text-slate-900 mb-1">{achievement.title}</p>
                    <p className="text-sm text-slate-600 mb-2">{achievement.impact}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {achievement.skills.map((skill, i) => (
                        <span 
                          key={i}
                          className="px-2 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-600"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Development Areas */}
            <div className="mb-6">
              <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium flex items-center gap-2">
                <Target className="w-4 h-4" />
                Development Areas
              </h4>
              <ul className="space-y-2">
                {reviewData.development_areas.items.map((area, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="text-slate-400 mt-0.5">•</span>
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Goals */}
            <div>
              <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Goals for Next Period
              </h4>
              <ul className="space-y-2">
                {reviewData.goals.items.map((goal, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="text-slate-400 mt-0.5">•</span>
                    <span>{goal}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={handleGoToDashboard}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3"
          >
            <Edit3 className="w-4 h-4 mr-2" />
            Edit & Refine in Dashboard
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard/living-cv')}
            className="px-6 py-3"
          >
            View Living CV
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* What's Next */}
        <div className="mt-12 text-center">
          <h3 className="font-semibold text-slate-900 mb-4">What's Next?</h3>
          <div className="grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
            <div className="bg-white p-4 rounded-xl border border-slate-200">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Edit3 className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-slate-900 mb-1">Refine Your Review</p>
              <p className="text-xs text-slate-500">Polish the language and add more detail</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-sm font-medium text-slate-900 mb-1">Build Your Living CV</p>
              <p className="text-xs text-slate-500">Track accomplishments over time</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Sparkles className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-sm font-medium text-slate-900 mb-1">Chat with Companion</p>
              <p className="text-xs text-slate-500">Reflect on your work weekly</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValueCompletionPage;
